import { Box, Grid } from '@mui/material'
import usePractitionerReportInsights from './usePractitionerReportInsights'
import PeriodComparison from './PeriodComparison'
import {
  INSIGHT_COLORS,
  InsightsCard,
  InsightsLoading,
  RatePill,
  SectionEmpty,
  StatTileRow,
  TopKBarChart,
} from './insightsCharts'

/**
 * Screener (ROLE_SCREENER) insights from the practitioner's own ScreeningReports: top flags,
 * disorder types, specializations, diagnoses and assessments, plus volume, new vs repeat (from the
 * report's own visitType) and the suggested-therapy rate.
 */
export default function ScreeningInsights({
  active,
  clinicBrandId,
  clinicLocationId,
  from,
  to,
  primaryPeriod,
  comparison,
}) {
  const { data, loading, error } = usePractitionerReportInsights({
    enabled: active,
    reportType: 'screening-report',
    clinicBrandId,
    clinicLocationId,
    from,
    to,
  })

  if (loading && !data) return <InsightsLoading />
  if (error && !data) return <SectionEmpty label={error} />
  if (!data) return <SectionEmpty />

  const totals = data.totals || {}
  const rates = data.rates || {}

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {comparison ? (
        <PeriodComparison
          active={active}
          reportType="screening-report"
          clinicBrandId={clinicBrandId}
          clinicLocationId={clinicLocationId}
          primaryPeriod={primaryPeriod}
          primaryData={data}
          comparison={comparison}
        />
      ) : null}

      <StatTileRow
        tiles={[
          { label: 'Screenings', value: totals.reports, color: INSIGHT_COLORS[0] },
          { label: 'Patients', value: totals.patients, color: INSIGHT_COLORS[6] },
          { label: 'New', value: totals.new, color: INSIGHT_COLORS[1] },
          { label: 'Repeat', value: totals.repeat, color: INSIGHT_COLORS[2] },
        ]}
      />
      <InsightsCard title="Outcomes & documentation" subtitle="Share of screenings with each">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <RatePill label="Suggested therapy booking" value={rates.suggestedTherapy} />
          <RatePill label="Diagnosis recorded" value={rates.diagnosisRecorded} />
          <RatePill label="Flags raised" value={rates.flagsRaised} />
          <RatePill label="Assessments conducted" value={rates.assessmentsConducted} />
        </Box>
      </InsightsCard>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Flags">
            <TopKBarChart items={data.topFlags} color={INSIGHT_COLORS[4]} emptyLabel="No flags raised" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Disorder Types">
            <TopKBarChart items={data.topDisorderTypes} color={INSIGHT_COLORS[1]} emptyLabel="No disorder types recorded" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Specializations">
            <TopKBarChart items={data.topSpecializations} color={INSIGHT_COLORS[3]} emptyLabel="No specializations recorded" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Diagnoses">
            <TopKBarChart items={data.topDiagnoses} color={INSIGHT_COLORS[0]} emptyLabel="No diagnoses recorded" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Assessments">
            <TopKBarChart items={data.topPsychAssessmentTitles} color={INSIGHT_COLORS[5]} emptyLabel="No assessments recorded" />
          </InsightsCard>
        </Grid>
      </Grid>
    </Box>
  )
}
